import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { getHealth } from '../api/client'
import type { Health } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import PasswordDialog from '../auth/PasswordDialog'

/** 外壳向下传递的上下文：健康状态只在这里取一次，页面按需消费 */
export interface ShellContext {
  health: Health | null
  /** null = 正在探测 */
  online: boolean | null
}

const navClass = ({ isActive }: { isActive: boolean }) =>
  isActive ? 'nav__item nav__item--on' : 'nav__item'

export default function AppShell() {
  const [health, setHealth] = useState<Health | null>(null)
  const [online, setOnline] = useState<boolean | null>(null)

  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const [menuOpen, setMenuOpen] = useState(false)
  const [pwdOpen, setPwdOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let alive = true

    getHealth()
      .then((h) => {
        if (!alive) return
        setHealth(h)
        setOnline(true)
      })
      .catch(() => {
        if (!alive) return
        setOnline(false)
      })

    return () => {
      alive = false
    }
  }, [])

  // 点击菜单外部 / 按 Esc 关闭
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: MouseEvent) => {
      if (menuRef.current !== null && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const displayName =
    user === null ? '' : user.displayName !== '' ? user.displayName : user.username
  const initial = displayName.slice(0, 1).toUpperCase()

  async function doLogout() {
    if (loggingOut) return
    setLoggingOut(true)
    setMenuOpen(false)
    try {
      await logout()
      navigate('/login', { replace: true })
    } finally {
      setLoggingOut(false)
    }
  }

  const pillClass = online === null ? 'pill' : online ? 'pill pill--ok' : 'pill pill--error'
  const pillText =
    online === null ? '连接中…' : online ? `已连接 v${health?.version ?? '?'}` : '未连接'

  return (
    <div className="app">
      <header className="app__bar">
        <div className="app__brand">
          <span className="app__logo">Friday</span>
          <span className="app__sub">本地看板</span>
        </div>

        <nav className="nav">
          <NavLink to="/" end className={navClass}>
            看板
          </NavLink>
          <NavLink to="/settings" className={navClass}>
            设置
          </NavLink>
        </nav>

        <span className={pillClass}>{pillText}</span>

        <div className="usermenu" ref={menuRef}>
          <button
            type="button"
            className="usermenu__btn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={displayName}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="usermenu__avatar" aria-hidden="true">
              {initial}
            </span>
            <span className="usermenu__name">{displayName}</span>
            <span className="usermenu__caret" aria-hidden="true">
              ▾
            </span>
          </button>

          {menuOpen && (
            <div className="usermenu__pop" role="menu">
              <button
                type="button"
                className="usermenu__item"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false)
                  setPwdOpen(true)
                }}
              >
                修改密码
              </button>
              <button
                type="button"
                className="usermenu__item usermenu__item--danger"
                role="menuitem"
                disabled={loggingOut}
                onClick={() => void doLogout()}
              >
                {loggingOut ? '正在退出…' : '退出登录'}
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="app__body">
        <Outlet context={{ health, online } satisfies ShellContext} />
      </main>

      <footer className="app__foot">
        dev: <code>npm run dev</code>（:5273，/api 代理到 :27180） · build:{' '}
        <code>npm run build</code> → <code>dist/</code> 由服务端托管
      </footer>

      {pwdOpen && <PasswordDialog onClose={() => setPwdOpen(false)} />}
    </div>
  )
}
